import React, { useState, useRef, useEffect, useCallback } from 'react';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { handleCheckQualityAudio } from '@src/services/instantVoiceCloning';
import useFakeProgress from '@src/hooks/useFakeProgress';
import CommonButton from '@src/new-components/Button';
import { MAX_DURATION, STEPS } from '@src/constants/instantVoiceCloning';
import WrapperStepContent from '../WrapperStepContent';
import Sentences from './Sentences';
import AudioRecorderStatus from './AudioRecorderStatus';
import RecordAgain from './RecordAgain';
import { StyledRecord } from './index.style';
import ProcessingAudio from '../ProcessingAudio';

const TIME_COUNT_DOWN = 3;
const ERRORS = {
  PERMISSION_DENIED: 'PermissionDeniedError',
  NOT_ALLOWED: 'NotAllowedError',
  NOT_FOUND: 'NotFoundError',
};
const FAKE_PROGRESS_INTERVAL = 100;
const MAX_FAKE_PROGRESS_TIME = 10000;

const VoiceRecord = ({
  onSetStep,
  metadataAudio,
  onSetMetadataAudio,
  fetchSentences,
  sentences,
  loadingSentences,
}) => {
  const { t } = useTranslation();
  const { progress, start, stop, reset } = useFakeProgress({
    interval: FAKE_PROGRESS_INTERVAL,
    maxProgress: 100,
    maxTime: MAX_FAKE_PROGRESS_TIME,
  });

  const [isRecording, setIsRecording] = useState(false);
  const [isCountdown, setIsCountdown] = useState(false);
  const [countdown, setCountdown] = useState(TIME_COUNT_DOWN);
  const [showStartText, setShowStartText] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [isMicAllowed, setIsMicAllowed] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const streamRef = useRef(null);
  const lastRecordedBlobRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const micPermissionPromiseRef = useRef(null);

  const clearStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      streamRef.current = null;
    }
  };

  const clearTimers = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const checkDeviceHasMic = useCallback(async () => {
    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.some((device) => device.kind === 'audioinput');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      return err.name !== ERRORS.NOT_FOUND;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isMicAllowed === null && navigator?.permissions) {
      navigator.permissions
        .query({ name: 'microphone' })
        .then(async (result) => {
          if (cancelled) return;
          const hasMic = await checkDeviceHasMic();
          setIsMicAllowed(result.state === 'granted' && hasMic);
        })
        .catch(() => setIsMicAllowed(false));
    }
    return () => {
      cancelled = true;
    };
  }, [isMicAllowed, checkDeviceHasMic]);

  useEffect(() => () => reset(), [reset]);

  useEffect(
    () => () => {
      clearStream();
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
      clearTimers();
    },
    [],
  );

  const handleRequestMic = useCallback(async () => {
    setIsProcessing(true);
    if (micPermissionPromiseRef.current) {
      await micPermissionPromiseRef.current;
      setIsProcessing(false);
      return;
    }
    micPermissionPromiseRef.current = navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        setIsMicAllowed(true);
        stream.getTracks().forEach((track) => track.stop());
        setIsMicPermissionDenied(false);
        setAudioError(null);
        setIsProcessing(false);
        setIsCountdown(true);
        setCountdown(TIME_COUNT_DOWN);
        setShowStartText(false);
      })
      .catch((err) => {
        if (
          [
            ERRORS.NOT_ALLOWED,
            ERRORS.PERMISSION_DENIED,
            ERRORS.NOT_FOUND,
          ].includes(err.name)
        ) {
          setIsMicPermissionDenied(true);
          setAudioError(t('micPermissionDenied'));
        }
        setIsMicAllowed(false);
        setIsProcessing(false);
      })
      .finally(() => {
        micPermissionPromiseRef.current = null;
      });
    await micPermissionPromiseRef.current;
  }, [t]);

  const handleStart = useCallback(() => {
    setAudioError(null);
    if (isMicAllowed) {
      setIsCountdown(true);
      setCountdown(TIME_COUNT_DOWN);
      setShowStartText(false);
    } else {
      handleRequestMic();
    }
  }, [isMicAllowed, handleRequestMic]);

  const handleStartRecording = useCallback(async () => {
    setIsRecording(true);
    setRecordTime(0);
    audioChunks.current = [];
    onSetMetadataAudio(null);
    setAudioError(null);
    lastRecordedBlobRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new window.MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/wav' });
        lastRecordedBlobRef.current = blob;
        clearStream();
      };

      recorder.start();
    } catch {
      setIsRecording(false);
    }
  }, [onSetMetadataAudio]);

  const handleStopRecording = useCallback(() => {
    setIsLoading(true);
    setAudioError(null);
    start();
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
    setTimeout(async () => {
      const blob = lastRecordedBlobRef.current;
      const url = URL.createObjectURL(blob);
      lastRecordedBlobRef.current = null;
      setIsRecording(false);

      const result = await handleCheckQualityAudio({ audioRecord: url });
      stop();
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsLoading(false);

      const { isQuality, audioId, s3Url } = result || {};

      if (isQuality) {
        onSetMetadataAudio({
          audioLink: s3Url,
          audioId,
          recordTime: recordTime || MAX_DURATION,
          fileName: `${moment().format('YYYY-MM-DD HH:mm:ss')}.wav`,
        });
        setAudioError(null);
        setIsMicPermissionDenied(false);
        onSetStep(STEPS.PREVIEW_AUDIO);
      } else {
        onSetMetadataAudio(null);
        setAudioError(t('invalidNoAudio'));
      }
    }, 100);
  }, [onSetMetadataAudio, onSetStep, recordTime, start, stop, t]);

  useEffect(() => {
    if (isCountdown && countdown > 0) {
      countdownTimerRef.current = setTimeout(
        () => setCountdown((c) => c - 1),
        1000,
      );
    }
    if (isCountdown && countdown === 0) {
      setShowStartText(true);
      countdownTimerRef.current = setTimeout(() => {
        setIsCountdown(false);
        setShowStartText(false);
        handleStartRecording();
      }, 1000);
    }
    return () => {
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [isCountdown, countdown, handleStartRecording]);

  const handleReset = useCallback(() => {
    onSetMetadataAudio(null);
    setRecordTime(0);
    setIsRecording(false);
    setIsCountdown(false);
    setCountdown(TIME_COUNT_DOWN);
    setShowStartText(false);
    setAudioError(null);
    setIsMicPermissionDenied(false);
    audioChunks.current = [];
    mediaRecorderRef.current = null;
    lastRecordedBlobRef.current = null;
    clearStream();
    clearTimers();
  }, [onSetMetadataAudio]);

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordTime((prev) => {
          if (prev >= MAX_DURATION) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
            setTimeout(handleStopRecording, 0);
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    };
  }, [isRecording, handleStopRecording]);

  if (isLoading) {
    return <ProcessingAudio title={t('processingAudio')} progress={progress} />;
  }

  const showRecorderStatus = isCountdown || showStartText || isRecording;
  const showStartButton =
    !isRecording &&
    !isCountdown &&
    !showStartText &&
    !metadataAudio?.audioLink &&
    !audioError;

  return (
    <WrapperStepContent
      showReloadButton
      onSetStep={onSetStep}
      onReload={fetchSentences}
      disabledButtonRefetch={isRecording || isCountdown || loadingSentences}
    >
      <StyledRecord>
        <Sentences loadingSentences={loadingSentences} sentences={sentences} />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {showRecorderStatus && (
            <AudioRecorderStatus
              handleStopRecording={handleStopRecording}
              recordTime={recordTime}
              isRecording={isRecording}
              isCountdown={isCountdown}
              countdown={showStartText ? t('start') : countdown}
              handleReset={handleReset}
            />
          )}
          {audioError && (
            <RecordAgain
              audioError={audioError}
              isMicPermissionDenied={isMicPermissionDenied}
              handleReset={handleReset}
              handleRequestMic={handleRequestMic}
            />
          )}
          {showStartButton && (
            <CommonButton
              variant="contained"
              textProps={{
                variantText: 'body-14-bold',
                color: COLOR.darkBlue,
              }}
              customStyle={{
                width: 'max-content',
                margin: '0 auto',
                height: '48px',
                padding: '12px 40px',
              }}
              onClick={handleStart}
              disabled={isProcessing || !sentences?.id || loadingSentences}
            >
              {t('startRecordingV2')}
            </CommonButton>
          )}
        </div>
      </StyledRecord>
    </WrapperStepContent>
  );
};

export default VoiceRecord;
